import React from 'react';
import { Loader2 } from 'lucide-react';
import { ContractSection, ContractSubSection } from '../../services/Contract';
import { useI18n } from '../../i18n';

const SubSections: React.FC<{ items?: ContractSubSection[]; depth?: number }> = ({ items, depth = 0 }) =>
  items?.length ? (
    <div className={depth > 0 ? 'ml-3' : ''}>
      {items.map((sub, i) => (
        <div key={i} className="mt-1.5">
          {sub.title && <p className="font-medium text-foreground/90">{sub.title}</p>}
          {sub.content && <p className="whitespace-pre-wrap">{sub.content}</p>}
          <SubSections items={sub.subSections} depth={depth + 1} />
        </div>
      ))}
    </div>
  ) : null;

/**
 * The contract while it's being written: sections appear as they finish
 * (they're written in parallel, so not necessarily in order).
 */
export const ContractLivePreview: React.FC<{ documentType: string; sections: ContractSection[] }> = ({
  documentType,
  sections,
}) => {
  const { t } = useI18n();
  const written = sections.filter(s => s.content || s.subSections?.length).length;
  return (
    <div className="rounded-xl border border-border bg-background shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b bg-muted/30 flex items-center justify-between gap-3">
        <span className="text-sm font-semibold text-foreground truncate">{documentType}</span>
        <span className="flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
          <Loader2 className="h-3 w-3 animate-spin" />
          {t.qagentChat.contract.writingProgress(written, sections.length)}
        </span>
      </div>
      <div className="max-h-[55vh] overflow-y-auto px-5 py-4 space-y-4 text-xs leading-relaxed text-muted-foreground">
        {sections.map((section, idx) => {
          const done = !!(section.content || section.subSections?.length);
          return (
            <section key={idx} className={done ? '' : 'opacity-60'}>
              <h4 className="text-sm font-semibold text-foreground">
                {idx + 1}. {section.title}
              </h4>
              {done ? (
                <>
                  {section.content && <p className="mt-1 whitespace-pre-wrap">{section.content}</p>}
                  <SubSections items={section.subSections} />
                </>
              ) : (
                <div className="mt-1.5 space-y-1.5" aria-label={t.qagentChat.contract.writingSection}>
                  <div className="h-2 w-11/12 rounded bg-muted animate-pulse" />
                  <div className="h-2 w-3/4 rounded bg-muted animate-pulse" />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};
